import { PAVE_GLSL } from "./groundPave";

/**
 * The ground surface's fragment code (groundMesh.ts patches it into a MeshStandardMaterial).
 *
 * This shader runs on most of the screen, so its cost per pixel is most of the frame's GPU time on a phone. Every
 * term the albedo, normal, roughness and lighting chunks share is computed ONCE in the map chunk and read back by the
 * later chunks (the splat, the slope map, the road parallax), and every layer costs only where it shows:
 * - nothing but the haze runs where the haze has fully taken the ground (`gLive`);
 * - the road is marched, sampled and normal-mapped only where the splat says road (red/blue), and the march runs once
 *   and is reused by the normal and the emissive cracks;
 * - the worn dirt only inside the shoulder mask, trodden earth under a tower (through the worn layer), rock and its noisy edge only
 *   where the slope can reach the rock line;
 * - the triplanar side planes only where the ground tilts past the flat floor (see triWeights).
 * Every skipped term was multiplied by a weight that is exactly zero there, so the image is the same.
 *
 * Inside those branches the textures are sampled with explicit gradients (textureGrad): derivatives are undefined in
 * non-uniform control flow, and a wrong mip at a branch edge would show as a line of sparkle along the road's rim.
 *
 * The look itself — the biome grass palette with its dry/lush macro ramp, the built road with its kerb and tower
 * pads, baked occlusion standing in for GTAO, wind bands, cloud shadows, ash cracks and lava glow — is the art pass's
 * (WOW ground shader pass, terrain F6–F11).
 *
 * This file holds the declarations and the albedo chunk; the chunks after it (emissive, lights, roughness, normal,
 * haze), which read the terms the albedo chunk sets, are in groundGlsl.ts.
 */

/**
 * Declarations and helpers, spliced after `#include <common>`. `S` is the world units one texture repeat covers;
 * `shared` is GroundMesh.SharedGlsl (uBounds/uHaze/uFog/uWindTime, beyondMap, the lava glow), `clouds` the
 * CloudShadows chunk and `wind` GroundMesh.windGlsl.
 */
export function groundCommon(S: number, shared: string, clouds: string, wind: string): string {
  return `
  varying vec3 vWP; varying vec3 vEye; varying vec2 vUnwarped; float vHaze;
  uniform sampler2D uDirt, uRoad, uRoadN, uSplat, uRock, uRockN;
  #ifdef ROAD_HGT
  uniform sampler2D uRoadH;
  #endif
  #ifdef GRASS_HGT
  uniform sampler2D uGrassH;
  #endif
  #ifdef GROUND_EMI
  uniform sampler2D uEmiA, uEmiR;
  #endif
  #ifdef GROUND_SLOPE
  uniform sampler2D uSlope;
  #endif
  uniform vec4 uSlopeBox;
  uniform float uPreviewGlow;
  uniform float uEmiOn; uniform vec3 uEmiColour; uniform vec3 uRoadTint;
  uniform vec3 uGrassGain, uDryGain, uLushGain; uniform float uGrassDesat;
  ${PAVE_GLSL}
  ${shared}
  ${clouds}
  ${wind}
  const float GROUND_S = ${(1 / S).toFixed(6)};
  float gnHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float gnNoise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(gnHash(i), gnHash(i + vec2(1, 0)), f.x), mix(gnHash(i + vec2(0, 1)), gnHash(i + vec2(1, 1)), f.x), f.y); }
  // The per-pixel terms the chunks share, computed once in map_fragment.
  vec4 gSlopeT;    // the slope map texel: normal xz, curvature, occlusion
  vec3 gN;         // the height field's normal
  vec3 gTriW;      // triplanar weights
  vec2 gUv, gRUv, gPadUv;
  float gRoad, gPad, gSteep, gWorn, gKerb, gOcc, gGrassW;
  // a road skin's paving (groundPave.ts): in a joint, the lit joints' weight, the stones' rim slope (world xz)
  float gPaveJ, gPaveGlow; vec2 gPaveTilt;
  bool gLive;      // false where the haze has fully taken the ground
  // Screen-space derivatives of the top-down UV (gUv = world XZ / S) and of the world position, taken once in uniform
  // control flow; a layer sampled at k * gUv has gradients k * gUvDx.
  vec2 gUvDx, gUvDy; vec3 gWPDx, gWPDy;
  vec4 texK(sampler2D t, vec2 uv, float k) { return textureGrad(t, uv, gUvDx * k, gUvDy * k); }
  vec4 splatAt(vec2 xz) { return textureGrad(uSplat, (xz - uBounds.xy) / uBounds.zw, gWPDx.xz / uBounds.zw, gWPDy.xz / uBounds.zw); }
  float roadAt(vec2 xz) { return smoothstep(0.42, 0.58, splatAt(xz).r); }
  #ifdef ROAD_HGT
  // Steep parallax: march the view ray through the height field until it goes under the surface, so cobbles stand
  // proud of their mortar. Only with a real height map. k is the layer's scale over gUv, for the gradients.
  vec2 parallax(sampler2D hmap, vec2 uv, float k, float scale) {
    vec3 v = normalize(vEye);
    vec2 dir = v.xz / max(abs(v.y), 0.35) * scale;
    const int STEPS = 6;   // 10 cost ~30 extra texture taps a pixel: too much for a phone at 2x
    vec2 step = dir / float(STEPS);
    float layer = 1.0, dl = 1.0 / float(STEPS);
    vec2 cur = uv;
    float hcur = texK(hmap, cur, k).r;
    for (int i = 0; i < STEPS; i++) {
      if (hcur >= layer) break;
      cur -= step; layer -= dl; hcur = texK(hmap, cur, k).r;
    }
    vec2 prev = cur + step;
    float a = hcur - layer, b = texK(hmap, prev, k).r - layer - dl;
    return mix(cur, prev, clamp(a / max(1e-4, a - b), 0.0, 1.0));
  }
  #endif
  // One tiling map sampled at two scales and angles, blended at weight w (dualW): kills the visible repeat. The blend
  // stays near half-and-half and varies at a fine frequency, so it never swaps whole patches of the field.
  const mat2 DUAL_R = mat2(0.83 * 0.47, 0.56 * 0.47, -0.56 * 0.47, 0.83 * 0.47);
  float dualW(vec2 uv) { return 0.28 + 0.3 * gnNoise(uv * 1.1); }
  vec4 dual(sampler2D t, vec2 uv, float k, float w) {
    return mix(texK(t, uv, k), textureGrad(t, DUAL_R * uv, DUAL_R * gUvDx * k, DUAL_R * gUvDy * k), w);
  }
  /** The two side planes of a triplanar layer, plain samples at world scale s (parallax buys nothing on a wall). */
  vec3 triSides(sampler2D t, float s, vec3 w) {
    vec3 c = textureGrad(t, vWP.zy * s, gWPDx.zy * s, gWPDy.zy * s).rgb * w.x;
    return c + textureGrad(t, vWP.xy * s, gWPDx.xy * s, gWPDy.xy * s).rgb * w.z;
  }
  /**
   * One layer, triplanar: the top-down sample (dual, already parallaxed where wanted), plus the sides only where they
   * have weight — the 0.32 floor in the weights keeps anything near flat PURE top-down.
   */
  vec3 triSample(sampler2D t, vec2 uvY, float k, float dw, vec3 w) {
    vec3 c = dual(t, uvY, k, dw).rgb * w.y;
    if (w.y < 0.999) c += triSides(t, GROUND_S * k, w);
    return c;
  }
  /**
   * triSample for a normal map. Below ultra (GROUND_NORMAL_SINGLE, QualityTier.groundNormalDual) its top-down plane
   * is one sample, not the anti-repeat pair: under the albedo's own pair the normal's repeat does not show.
   */
  vec3 triSampleN(sampler2D t, vec2 uvY, float k, float dw, vec3 w) {
    #ifdef GROUND_NORMAL_SINGLE
    vec3 c = texK(t, uvY, k).rgb * w.y;
    if (w.y < 0.999) c += triSides(t, GROUND_S * k, w);
    return c;
    #else
    return triSample(t, uvY, k, dw, w);
    #endif
  }
`;
}

/** Albedo, spliced over `#include <map_fragment>`. Sets the per-pixel terms the later chunks reuse. */
export function groundMap(rampReach: number): string {
  return `
  gUv = vWP.xz * GROUND_S;
  gUvDx = dFdx(gUv); gUvDy = dFdy(gUv); gWPDx = dFdx(vWP); gWPDy = dFdy(vWP);
  // The map has no edge. The ground runs well past the playable rectangle and dissolves into haze — the fog of war.
  // Where the haze has fully taken it, the layers below would be mixed away to nothing, so they are not computed.
  vHaze = smoothstep(uFog.x, uFog.y, beyondMap(vWP.xz));
  gLive = vHaze < 0.999;
  // The ground's normal from the baked slope map (groundSlope.ts), looked up at the pixel's UNWARPED position (the uv
  // attribute carries it). NOT cross(dFdx, dFdy) or the vertex normal: both put the rock line and the triplanar
  // weights on the triangle edges. Its other two channels are curvature and baked occlusion.
  #ifdef GROUND_SLOPE
  gSlopeT = texture2D(uSlope, (vUnwarped - uSlopeBox.xy) / uSlopeBox.zw);
  vec2 nxz = gSlopeT.rg * 2.0 - 1.0;
  gN = vec3(nxz.x, sqrt(max(0.0, 1.0 - dot(nxz, nxz))), nxz.y);
  #else
  gSlopeT = vec4(0.5, 0.5, 0.5, 1.0);                  // over the sampler budget: flat curvature, no occlusion
  gN = normalize(cross(gWPDx, gWPDy));
  #endif
  float curv = gSlopeT.b * 2.0 - 1.0;                  // + convex (bench tops, shoulders), - concave (feet)
  gOcc = gSlopeT.a;
  gTriW = vec3(0.0, 1.0, 0.0); gRoad = 0.0; gPad = 0.0; gSteep = 0.0; gWorn = 0.0; gKerb = 0.0; gGrassW = 0.5;
  gPaveJ = 0.0; gPaveGlow = 0.0; gPaveTilt = vec2(0.0);
  gRUv = gUv * 4.1; gPadUv = gUv * 2.05 + vec2(0.37, 0.61);
  float preview = 0.0;
  if (gLive) {
    // Weights for the triplanar projection: top-down stretches by 1/|n.y| down a slope and smeared the cobbles into
    // strands; the side projections fix it. Anything near flat stays pure top-down.
    vec3 tw = max(abs(gN) - 0.32, vec3(0.0));
    tw *= tw;
    gTriW = tw / max(1e-4, tw.x + tw.y + tw.z);
    // Bare rock only on ground steeper than a walkable ramp (the meadow's ramps reach ~0.45). Two octaves of noise rag
    // the line; they move it by at most 0.075, so ground flatter than 0.40 is grass whatever the noise says.
    float slopeAmt = 1.0 - abs(gN.y);
    if (slopeAmt >= 0.40) {
      float wobble = (gnNoise(gUv * 3.3) - 0.5) * 0.10 + (gnNoise(gUv * 11.0 + 4.0) - 0.5) * 0.05;
      gSteep = smoothstep(0.48, 0.66, slopeAmt + wobble);
    }

    vec4 sp = splatAt(vWP.xz);
    // A built road has an edge: the splat is 32 px a cell and the edge a narrow band either side of the stroke's
    // half-coverage line.
    gRoad = smoothstep(0.42, 0.58, sp.r);
    // Blue is the road card's preview: the tiles it would lay, painted as real road, lifted and warmed (below) so it
    // reads as not-yet-built.
    preview = smoothstep(0.42, 0.58, sp.b) * (1.0 - gRoad);
    gRoad = max(gRoad, preview);
    // The kerb: a thin band along the road's edge that darkens (the gap where the stones meet the turf) and tilts the
    // normal so the track sits a centimetre or two proud of the grass (normal chunk).
    gKerb = gRoad * (1.0 - gRoad) * 4.0;
    gPad = smoothstep(0.1, 0.9, sp.a) * (1.0 - gRoad);
    // The road is sampled straight, not through dual() (whose half-scale second sample turned the cobbles into
    // half-cell paving slabs), and top-down only (the side projections ghosted a second set of cobbles on a ramp).
    #ifdef ROAD_HGT
    if (gRoad > 0.01) gRUv = parallax(uRoadH, gRUv, 4.1, 0.03);
    #endif

    // Worn dirt lives in the shoulder mask only, frayed by noise so it breaks into the grass instead of glowing round
    // the road as a halo; plus a thin skin of exposed soil on the convex shoulder of a real ramp.
    float fray = gnNoise(gUv * 7.0) * 0.6 + gnNoise(gUv * 19.0 + 3.0) * 0.4;
    gWorn = smoothstep(0.30, 0.75, sp.g + (fray - 0.5) * 0.55) * (1.0 - gRoad);
    gWorn = max(gWorn, smoothstep(0.35, 0.8, curv) * smoothstep(0.22, 0.4, slopeAmt) * 0.55 * (1.0 - gRoad));
    // Under a tower: trodden earth, frayed into the grass like the road's verge (Kevin, 10-07: the old flagstone pad
    // was the road's own cobbles, so on a hillside — stretched down the slope, where the footing does not cover it —
    // it read as a broken piece of road). It thins on a slope, where the turf holds and the rock layer takes over.
    if (gPad > 0.001) {
      float padFray = gnNoise(gUv * 11.0 + 5.0) * 0.6 + gnNoise(gUv * 23.0 + 1.7) * 0.4;
      float padW = smoothstep(0.15, 0.75, gPad + (padFray - 0.5) * 0.6) * (1.0 - 0.7 * smoothstep(0.12, 0.35, slopeAmt));
      gWorn = max(gWorn, padW * 0.85);
    }

    // Grass is sampled denser than the other layers: at one repeat per 4 units its clump pattern magnified into a pale
    // net of cracks up close; at ~1.7 units a clump is a clump.
    vec2 grUv = gUv * 2.3;
    gGrassW = dualW(grUv);
    vec3 grassC = triSample(map, grUv, 2.3, gGrassW, gTriW);
    // Grass colour (GroundMesh.GrassPalette): the map's average is mapped onto the biome's base colour, its own detail
    // riding on top, and a macro ramp swings it toward dry straw or lush blue-green — pushed dry on convex ground and
    // lush where the occlusion channel says something stands close by, or the ground is a hollow.
    grassC *= uGrassGain;
    float gLum = dot(grassC, vec3(0.2126, 0.7152, 0.0722));
    grassC = mix(grassC, vec3(gLum), uGrassDesat);
    float macro = gnNoise(vWP.xz * 0.07) * 0.6 + gnNoise(vWP.xz * 0.19 + 7.3) * 0.4;
    float lushNear = 1.0 - smoothstep(0.90, 0.985, gOcc);
    float ramp = clamp((macro - 0.5) * 3.0 + curv * 0.55 - lushNear * 0.9, -1.0, 1.0);
    grassC *= ramp > 0.0 ? mix(vec3(1.0), uDryGain, ramp * ${rampReach.toFixed(2)})
                         : mix(vec3(1.0), uLushGain, -ramp * ${rampReach.toFixed(2)});
    // convex ground catches more sky and is lighter; hollows are 15% darker (on top of the occlusion)
    grassC *= 1.0 + 0.07 * max(curv, 0.0) - 0.15 * max(-curv, 0.0);
    // Wind bands comb the meadow (terrain F7): a travelling band lifts the grass and warms it toward yellow, as the
    // blades bend and show their paler undersides. The props sway to the same band.
    float band = windBand(vWP.xz, uWindTime) * (1.0 - gSteep);
    grassC *= 1.0 + band * vec3(0.10, 0.07, -0.02);
    #ifdef GRASS_HGT
    grassC *= mix(0.88, 1.04, smoothstep(0.15, 0.8, texK(uGrassH, grUv, 2.3).r));
    #else
    grassC *= 0.97;                                       // no height map: the crevice term's average, not a guess
    #endif

    vec3 col = grassC;
    if (gWorn > 0.001) {
      // The shoulder is warm dark earth: it frames the road instead of haloing it. No parallax on a worn verge.
      vec2 dUv = gUv * 1.15;
      vec3 dirtC = triSample(uDirt, dUv, 1.15, dualW(dUv), gTriW);
      dirtC = mix(dirtC, vec3(0.195, 0.102, 0.042), 0.45);
      col = mix(col, dirtC, gWorn * 0.6);
    }
    if (gSteep > 0.001) { vec2 kUv = gUv * 1.6; col = mix(col, triSample(uRock, kUv, 1.6, dualW(kUv), gTriW), gSteep * (1.0 - gRoad)); }
    if (gRoad > 0.01) {
      // Road tint at or under 1: the sunlit road was the brightest thing on screen and the bloom caught it as a halo.
      vec3 roadC = texK(uRoad, gRUv, 4.1).rgb * uRoadTint;      // skins/roadSkin.ts: the stock grade × the road skin
      #ifdef ROAD_HGT
      // crevice occlusion: whatever sits low in the height field is in shade — the mortar between the stones.
      roadC *= mix(0.72, 1.06, smoothstep(0.08, 0.62, texK(uRoadH, gRUv, 4.1).r));
      #endif
      if (uPave.x > 0.5) {
        // a road skin's own paving (groundPave.ts) in place of the cobble: its stones' grain is the rock map's,
        // normalised by that map's smallest mip so every biome's rock gives the same contrast
        vec2 kUv = gUv * 3.1;
        const vec3 LUM = vec3(0.2126, 0.7152, 0.0722);
        float gl = dot(texK(uRock, kUv, 3.1).rgb, LUM);
        float gm = dot(textureGrad(uRock, kUv, gUvDx * 160.0, gUvDy * 160.0).rgb, LUM);
        float aa = max(length(gWPDx.xz), length(gWPDy.xz));
        roadC = tdPave(vWP.xz, aa, clamp(gl / max(gm, 0.02), 0.55, 1.45), gPaveJ, gPaveTilt, gPaveGlow);
      }
      col = mix(col, roadC, gRoad);
    }
    col *= 1.0 - 0.4 * gKerb;
    col = mix(col, col * vec3(1.22, 1.12, 0.86) + vec3(0.07, 0.05, 0.0), preview * uPreviewGlow);
    // a gentle value drift over everything, so even the road and the rock are not one flat tone
    col *= 0.96 + 0.08 * macro;
    diffuseColor.rgb *= col;
  }
  diffuseColor.rgb = mix(diffuseColor.rgb, uHaze, vHaze);`;
}
